'use client';

import React from 'react';
import useCoinData from '@/utils/coins/useCoinData';
import { readClosedRanges } from '@/utils/coins/marketClosed';
import { CHART_DEFAULT_ORDER } from '@/utils/coins/chartRanges';
import BackToButton from '@/components/(postTypes)/platforms/(archive)/layout/BackToButton';
import Section from '@/components/(postTypes)/symbols/Section';
import { CoinProps } from '@/types/coins';
import { PageContent } from '@/app/(site)/(postTypes)/symbol/[slug]/page';
import { HistoryDataProps, Timeframes } from '@/types/coins/history';
import SingleCoinExistRelatedPost from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinExistRelatedPost';
import {
    CommentNode,
    CommentsData,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reviews';
import { Exchange } from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinExchangesList';
import { RelatedPostsData } from '@/utils/coins/fetchAditionalSymbolSingleData';
import SingleCoinHeader from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinHeader';
import SymbolHeader from '@/components/(postTypes)/symbols/single/forex/layout/SymbolHeader';
import SymbolAnalysis from '@/components/(postTypes)/symbols/single/forex/layout/SymbolAnalysis';
import PostEditorsChoice from '@/components/(blog)/post/PostEditorsChoice';
import { useStickyBox } from 'react-sticky-box';
import dynamic from 'next/dynamic';
import SingleCoinExchangesListLive from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinExchangesListLive';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SingleCoinIntro from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinIntro';
import Image from 'next/image';
import MainContent from '@/components/(blog)/MainContent';
import PostFAQ from '@/components/(blog)/post/PostFAQ';
import PostToc from '@/components/(blog)/post/PostToc';
import { extractTocItems } from '@/utils/post/extractTocItems';
import PostComments from '@/components/(blog)/post/PostComments';
import { DocumentText, ArrowDown2 } from 'iconsax-react';
import LazySingleCoinMainChart from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinMainChart';
import { SymbolBreadcrumb } from '@/components/(postTypes)/symbols/single/SymbolBreadcrumb';
import { SingleCoinMobileNav } from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinMobileNav';
import MainRenderSection from '@/components/(postTypes)/symbols/single/coin/layout/RenderSection';
import MarketLiveWidget, {
    MarketLiveWidgetGroup,
} from '@/components/(postTypes)/symbols/single/MarketLiveWidget';
import { getAssetChartSymbol } from '@/utils/coins/getAssetChartSymbol';
import SymbolLinks, { SymbolLinkGroup } from '@/components/(postTypes)/symbols/single/SymbolLinks';
import SymbolDailyTable from '@/components/(postTypes)/symbols/single/SymbolDailyTable';
import type { DailyPriceTable } from '@/utils/coins/dailyPriceRows';

const LazyBitycleChart = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinBitycleChart'),
    {
        ssr: false,
        loading: () => (
            <p className='text-center p-6 text-gray-500'>در حال بارگذاری تحلیل تکنیکال ...</p>
        ),
    },
);
const LazySingleCoinSentiment = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinSentiment'),
    {
        ssr: false,
        loading: () => <p className='p-2 text-xs text-gray-400'>در حال بارگذاری شاخص احساسات...</p>,
    },
);
const LazySingleCoinInvestorGrade = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinInvestorGrade'),
    { ssr: false, loading: () => null },
);
const LazySingleCoinTMGrade = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinTMGrade'),
    { ssr: false, loading: () => null },
);
const LazySingleCoinExchange = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinExchange'),
    {
        ssr: false,
        loading: () => <p className='p-4 text-center text-gray-400'>در حال بارگذاری مبدل...</p>,
    },
);
const LazySingleCoinTableStatus = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinTableStatus'),
    {
        ssr: false,
        loading: () => (
            <p className='p-4 text-center text-gray-400'>در حال بارگذاری آمار بازار...</p>
        ),
    },
);
const LazySingleCoinPrediction = dynamic(
    () => import('@/components/(postTypes)/symbols/single/coin/layout/SingleCoinPrediction'),
    {
        ssr: false,
        loading: () => (
            <p className='p-4 text-center text-gray-400'>در حال بارگذاری پیش بینی قیمت...</p>
        ),
    },
);

export type UniversalSymbolType = 'coin' | 'crypto' | 'gold' | 'currency' | 'currencies' | 'forex';

export interface UniversalCoinPageRenderProps {
    symbolType: UniversalSymbolType;
    data?: CoinProps;
    slug: string;
    PageContent?: PageContent['symbolBy'] | any;
    historyData?: HistoryDataProps;
    comments?: CommentNode[];
    hasNextPage?: CommentsData['comments']['pageInfo']['hasNextPage'];
    exchangeList?: Exchange[];
    singleCoinRelatedPosts?: RelatedPostsData;
    marketWidgetGroups?: MarketLiveWidgetGroup[];
    symbolLinks?: SymbolLinkGroup[];
    dailyPrices?: DailyPriceTable;
}

const UniversalCoinPageRender: React.FC<UniversalCoinPageRenderProps> = ({
    symbolType,
    data,
    slug,
    PageContent,
    historyData,
    comments,
    hasNextPage,
    exchangeList,
    singleCoinRelatedPosts,
    marketWidgetGroups,
    symbolLinks,
    dailyPrices,
}) => {
    const normalizedType: 'forex' | 'gold' | 'currency' | 'crypto' =
        symbolType === 'forex'
            ? 'forex'
            : symbolType === 'gold'
              ? 'gold'
              : symbolType === 'currency' || symbolType === 'currencies'
                ? 'currency'
                : 'crypto';

    const stickyRef = useStickyBox({ offsetTop: 70, offsetBottom: 20 });

    const timeframes = historyData?.timeframes;
    const timeframeOrder = ['1h', '3h', '12h', '24h', '7d', '30d', '3m', '1y', '3y', '5y'];
    const activeTimePeriods = timeframeOrder.filter((period) => {
        if (timeframes && typeof timeframes === 'object') {
            return Boolean(timeframes[period as keyof Timeframes]);
        }
        if (data?.changes && typeof data.changes === 'object') {
            return period in (data.changes as any);
        }
        return ['24h', '7d', '30d', '1y', '5y'].includes(period);
    });
    // Charts open on 1 year (a longer line reads better); shorter ranges when a symbol has no yearly data
    const initialTimePeriod =
        CHART_DEFAULT_ORDER.find((p) => activeTimePeriods.includes(p)) ||
        activeTimePeriods[0] ||
        '1y';

    const {
        history,
        timePeriod,
        updateTimePeriod,
        error: historyError,
        historyPeriod,
    } = useCoinData({
        initialCoin: data,
        initialTimePeriod,
        type:
            normalizedType === 'gold'
                ? 'gold'
                : normalizedType === 'currency'
                  ? 'currency'
                  : 'crypto',
    });

    const [chartTab, setChartTab] = React.useState<string>('main-chart');
    const [isScrolledUp, setIsScrolledUp] = React.useState<boolean>(false);
    const [lastScrollTop, setLastScrollTop] = React.useState<number>(0);

    React.useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            if (scrollTop < lastScrollTop) {
                setIsScrolledUp(true);
            } else {
                setIsScrolledUp(false);
            }
            setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollTop]);

    const {
        name,
        nameFa,
        marketCap,
        dominance,
        supply,
        price,
        symbol,
        sentiment,
        typeId,
        coinrankingUuid,
        iconUrl,
        rank,
        investorGrade,
        traderGrade,
        hasExchange,
    } = data || {};

    const { content, commentCount, databaseId, postRatings, posts_acf, featuredImage, title } =
        PageContent || {};

    const fallbackDisplayName =
        normalizedType === 'gold'
            ? 'طلا و سکه'
            : normalizedType === 'currency'
              ? 'قیمت ارز'
              : normalizedType === 'forex'
                ? 'جفت ارز فارکس'
                : 'ارز دیجیتال';

    const displayName = nameFa || name || title || fallbackDisplayName;
    const volume24h = data?.['24hVolume'];

    const { tocItems } = extractTocItems(content || '');
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

    // --- FOREX RENDER ---
    if (normalizedType === 'forex') {
        const forexSlug = `OANDA:` + slug?.toUpperCase();
        return (
            <main className='min-h-screen bg-gray-50/50 dark:bg-primary-dark transition-colors duration-300 py-6 lg:py-10'>
                <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
                    <div className='bg-white dark:bg-primary-active border border-gray-100 dark:border-white/10 rounded-2xl p-4 lg:p-6 shadow-sm mb-8 text-center'>
                        <h1 className='text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                            {title || displayName}
                        </h1>
                        <p className='text-xs text-gray-500 dark:text-gray-400'>
                            تحلیل بازار فارکس و سیگنال تکنیکال
                        </p>
                    </div>

                    <SingleCoinMobileNav
                        nameFa={displayName}
                        name={name || title}
                        symbol={slug}
                        symbolType='forex'
                    />

                    <div className='bg-white dark:bg-primary-active border border-gray-100 dark:border-white/10 rounded-2xl p-4 lg:p-6 shadow-sm mb-8'>
                        <SymbolHeader slug={forexSlug} />
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8'>
                        <div
                            id='signal-section'
                            className='lg:col-span-4 bg-white dark:bg-primary-active border border-gray-100 dark:border-white/10 rounded-2xl p-4 lg:p-6 shadow-sm'
                        >
                            <Section title='سیگنال اتوماتیک'>
                                <SymbolAnalysis slug={forexSlug} />
                            </Section>
                        </div>
                        <div
                            id='chart-section'
                            className='lg:col-span-8 bg-white dark:bg-primary-active border border-gray-100 dark:border-white/10 rounded-2xl p-4 lg:p-6 shadow-sm'
                        >
                            <Section title='چارت تحلیل تکنیکال'>
                                <LazyBitycleChart slug={forexSlug} symbolType={symbolType} />
                            </Section>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 items-start'>
                        <div
                            id='blog-content-section'
                            className='lg:col-span-8 bg-white dark:bg-primary-active border border-gray-100 dark:border-white/10 rounded-2xl p-4 lg:p-6 shadow-sm'
                        >
                            <article className='prose post-content max-w-none text-justify dark:text-white mb-8'>
                                {tocItems && tocItems.length > 0 && (
                                    <PostToc
                                        tocItems={tocItems}
                                        className='bg-blue-50/50 dark:bg-white/5 border border-blue-100 dark:border-white/10 p-4 rounded-xl mb-6'
                                    />
                                )}
                                <MainContent
                                    processedContent={content || ''}
                                    className={undefined}
                                    title={title || displayName || ''}
                                />
                            </article>
                            <div id='comments-section'>
                                <PostComments
                                    currentPostId={databaseId || 0}
                                    postRatings={postRatings}
                                    commentCount={commentCount || 0}
                                />
                            </div>
                        </div>

                        <aside className='lg:col-span-4'>
                            <div ref={stickyRef}>
                                <PostEditorsChoice />
                            </div>
                        </aside>
                    </div>
                </div>
            </main>
        );
    }

    // --- GOLD & CURRENCY RENDER ---
    if (normalizedType === 'gold' || normalizedType === 'currency') {
        const backToTitle =
            normalizedType === 'gold'
                ? 'بازگشت به بازار طلا و سکه'
                : 'بازگشت به لیست قیمت ارزهای فیات';
        const assetChart = getAssetChartSymbol(symbol || slug, normalizedType, displayName);

        return (
            <div className='min-h-screen bg-gray-50/50 dark:bg-primary-dark transition-colors duration-300 py-4 lg:py-6'>
                <div className='customContainer max-lg:px-0'>
                    <SymbolBreadcrumb
                        breadcrumbs={PageContent?.seo?.breadcrumbs}
                        displayName={displayName}
                        symbolType={normalizedType}
                    />

                    <SingleCoinMobileNav
                        nameFa={nameFa}
                        name={name}
                        symbol={symbol}
                        iconUrl={iconUrl}
                        price={price}
                        priceUsd={data?.price_usd}
                        quoteCurrency={data?.quote_currency}
                        change={history?.change ?? data?.change ?? 0}
                        hasExchange={false}
                        symbolType={normalizedType}
                    />

                    {/* Desktop: content column (sticky header + main sections) and a sticky sidebar with the
                        live price list. Mobile: both columns are `contents`, so the order-* classes interleave them. */}
                    <div className='w-full flex flex-col items-stretch lg:grid lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_360px] gap-0 lg:gap-6 lg:items-start'>
                        <div className='contents lg:flex lg:flex-col lg:gap-6 lg:min-w-0'>
                            <SingleCoinHeader
                                slug={slug}
                                updateTimePeriod={updateTimePeriod}
                                timePeriod={timePeriod}
                                change={data?.change_24h ?? data?.change ?? 0}
                                changeToman={data?.change_toman}
                                nameFa={nameFa}
                                coinrankingUuid={coinrankingUuid}
                                iconUrl={iconUrl}
                                name={name || ''}
                                price={price}
                                rank={rank}
                                symbol={symbol || ''}
                                typeId={typeId}
                                isCurrencyOrGold={true}
                                inColumn
                                symbolType={normalizedType}
                                price_usd={data?.price_usd}
                                quote_currency={data?.quote_currency}
                            />

                            <section
                                id='chart-section'
                                className='bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                            >
                                <Tabs
                                    value={chartTab}
                                    onValueChange={setChartTab}
                                    className='relative w-full'
                                >
                                    {/* Tabs float over the right end of each chart's own toolbar row (h-7 at top-2),
                                        so tabs and chart tools share a single line; heading kept for SEO only */}
                                    <div
                                        dir='rtl'
                                        className='absolute top-2 right-0 z-20 flex h-7 items-center'
                                    >
                                        <h2 className='sr-only'>نمودار قیمت {displayName}</h2>
                                        <TabsList className='bg-gray-100 dark:bg-white/10 p-0.5 rounded-lg flex gap-0.5 h-auto w-auto'>
                                            <TabsTrigger
                                                value='main-chart'
                                                className='px-2.5 py-1 text-xs font-medium rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-white shadow-none transition-all text-center'
                                            >
                                                نمودار قیمت
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value='trading-view'
                                                className='px-2.5 py-1 text-xs font-medium rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-white shadow-none transition-all text-center'
                                            >
                                                تحلیل تکنیکال
                                            </TabsTrigger>
                                        </TabsList>
                                    </div>
                                    <TabsContent value='main-chart' className='mt-0'>
                                        <LazySingleCoinMainChart
                                            timePeriod={timePeriod}
                                            updateTimePeriod={updateTimePeriod}
                                            typeId={typeId || 2}
                                            change={history?.change}
                                            history={
                                                (history?.history as HistoryDataProps['history']) ||
                                                null
                                            }
                                            timeframes={timeframes}
                                            changes={data?.changes as any}
                                            loading={historyPeriod !== timePeriod}
                                            failed={!!historyError}
                                            displayName={displayName || ''}
                                            symbol={symbol}
                                            closedRanges={readClosedRanges(
                                                history?.market_closed_ranges,
                                            )}
                                            quoteCurrency={
                                                history?.quote_currency || data?.quote_currency
                                            }
                                        />
                                    </TabsContent>
                                    <TabsContent
                                        value='trading-view'
                                        className='mt-0 pt-2 min-h-[480px]'
                                    >
                                        <LazyBitycleChart
                                            slug={assetChart.symbol}
                                            symbolType={normalizedType}
                                            hideCurrencySelector
                                            height={460}
                                        />
                                        <p className='mt-2 text-[11px] text-gray-500 dark:text-gray-400'>
                                            نمودار تکنیکال {assetChart.label}
                                        </p>
                                    </TabsContent>
                                </Tabs>
                            </section>

                            {/* Mobile only: price + 24h toman change (desktop shows the toman change in the header) */}
                            <section
                                id='market-table-section'
                                className='order-3 lg:hidden bg-white dark:bg-primary-active px-4 sm:px-6 py-4 w-auto'
                            >
                                <h3 className='font-bold text-sm dark:text-white mb-2'>
                                    اطلاعات بازار {displayName}
                                </h3>
                                <LazySingleCoinTableStatus {...(data || {})} />
                            </section>

                            {(dailyPrices?.rows.length ?? 0) > 1 && (
                                <Section
                                    id='daily-prices-section'
                                    title={`قیمت ${displayName} در ۳۰ روز گذشته`}
                                    className='order-4 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                                >
                                    <SymbolDailyTable
                                        table={dailyPrices}
                                        symbol={symbol || slug}
                                        displayName={displayName || ''}
                                    />
                                </Section>
                            )}

                            <section
                                id='converter-section'
                                className='order-7 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-0 py-5 lg:py-0 w-auto lg:w-full min-w-0'
                            >
                                <LazySingleCoinExchange
                                    price={price?.usdt_price || 0}
                                    initialCoin={data}
                                />
                            </section>

                            <MainRenderSection
                                slug={slug}
                                updateTimePeriod={updateTimePeriod}
                                timePeriod={timePeriod}
                                timeframes={timeframes}
                                history={(history as HistoryDataProps['history']) || null}
                                change={history?.change}
                                nameFa={nameFa}
                                coinrankingUuid={coinrankingUuid}
                                iconUrl={iconUrl}
                                name={name || ''}
                                price={price}
                                price_usd={data?.price_usd}
                                quote_currency={data?.quote_currency}
                                rank={rank}
                                symbol={symbol || ''}
                                typeId={2}
                                displayName={displayName || ''}
                                marketCap={marketCap}
                                supply={supply}
                                dominance={dominance}
                                volume24h={volume24h}
                                content={content}
                                featuredImage={featuredImage}
                                posts_acf={posts_acf}
                                commentCount={commentCount || 0}
                                databaseId={databaseId || 0}
                                postRatings={postRatings}
                                hasExchange={hasExchange}
                                hasNextPage={hasNextPage}
                                comments={comments}
                            />

                            <Section
                                id='related-section'
                                title={`مقالات مرتبط با ${displayName}`}
                                className='order-10 lg:order-none lg:col-start-1 bg-gray-100/70 dark:bg-white/5 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full min-w-0'
                            >
                                <SingleCoinExistRelatedPost
                                    categoryName={slug}
                                    displayName={displayName || ''}
                                    initialData={singleCoinRelatedPosts}
                                />
                            </Section>

                            {(symbolLinks?.length ?? 0) > 0 && (
                                <Section
                                    id='other-prices-section'
                                    title={
                                        normalizedType === 'gold'
                                            ? 'قیمت طلا، سکه و ارزهای دیگر'
                                            : 'قیمت ارزهای دیگر، طلا و سکه'
                                    }
                                    className='order-11 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                                >
                                    <SymbolLinks
                                        groups={symbolLinks || []}
                                        currentSymbol={symbol || slug}
                                    />
                                </Section>
                            )}
                        </div>

                        {(marketWidgetGroups?.length ?? 0) > 0 && (
                            <aside
                                className={`contents lg:block lg:sticky lg:self-start z-20 transition-all ease-in-out duration-500 ${
                                    isScrolledUp ? 'lg:top-[124px]' : 'lg:top-[60px]'
                                }`}
                            >
                                <section
                                    id='market-widget-section'
                                    className={`order-2 lg:order-none bg-gray-100/70 dark:bg-white/5 px-4 sm:px-6 lg:p-4 py-5 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full min-w-0 lg:overflow-y-auto lg:overscroll-contain [scrollbar-width:thin] ${
                                        isScrolledUp
                                            ? 'lg:max-h-[calc(100vh-148px)]'
                                            : 'lg:max-h-[calc(100vh-84px)]'
                                    }`}
                                >
                                    <MarketLiveWidget
                                        groups={marketWidgetGroups || []}
                                        currentSymbol={symbol || slug}
                                    />
                                </section>
                            </aside>
                        )}
                        <BackToButton title={backToTitle} />
                    </div>
                </div>
            </div>
        );
    }

    // --- CRYPTO COIN RENDER ---
    return (
        <div className='min-h-screen bg-gray-50/50 dark:bg-primary-dark transition-colors duration-300 py-4 lg:py-6'>
            <div className='customContainer max-lg:px-0'>
                <SymbolBreadcrumb
                    breadcrumbs={PageContent?.seo?.breadcrumbs}
                    displayName={displayName}
                    symbolType='crypto'
                />

                <SingleCoinHeader
                    slug={slug}
                    updateTimePeriod={updateTimePeriod}
                    timePeriod={timePeriod}
                    change={data?.change ?? data?.change_24h ?? 0}
                    nameFa={nameFa}
                    coinrankingUuid={coinrankingUuid}
                    iconUrl={iconUrl}
                    name={name || ''}
                    price={price}
                    rank={rank}
                    symbol={symbol || ''}
                    typeId={typeId}
                    asset_type={data?.asset_type}
                    marketCap={marketCap || data?.marketCap}
                    volume24h={volume24h || data?.['24hVolume']}
                    dominance={dominance || data?.dominance}
                    symbolType='crypto'
                />

                <SingleCoinMobileNav
                    nameFa={nameFa}
                    name={name}
                    symbol={symbol}
                    iconUrl={iconUrl}
                    price={price}
                    change={history?.change || 0}
                    hasExchange={hasExchange}
                />

                <div className='w-full flex flex-col items-stretch lg:grid lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_380px] gap-0 lg:gap-6 lg:items-start'>
                    <section
                        id='chart-section'
                        className='order-1 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                    >
                        <Tabs value={chartTab} onValueChange={setChartTab} className='w-full'>
                            <div className='flex flex-col sm:flex-row items-end sm:items-start justify-between gap-3 border-b border-gray-100 dark:border-white/5 pb-3 mb-1 lg:mb-4'>
                                <TabsList className='bg-gray-100 dark:bg-white/10 p-1 rounded-xl flex gap-1 h-auto w-full sm:w-auto order-2 sm:order-1'>
                                    <TabsTrigger
                                        value='main-chart'
                                        className='flex-1 sm:flex-initial px-4 py-1.5 text-xs font-medium rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-white shadow-none transition-all text-center'
                                    >
                                        نمودار قیمت
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value='trading-view'
                                        className='flex-1 sm:flex-initial px-4 py-1.5 text-xs font-medium rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-white shadow-none transition-all disabled:opacity-40 text-center'
                                    >
                                        نمودار تحلیل تکنیکال
                                    </TabsTrigger>
                                </TabsList>
                                <h2 className='text-sm lg:text-base font-bold text-gray-900 dark:text-white order-1 sm:order-2'>
                                    نمودار قیمت و تحلیل تکنیکال {displayName}
                                </h2>
                            </div>
                            <TabsContent value='main-chart' className='mt-0'>
                                <LazySingleCoinMainChart
                                    timePeriod={timePeriod}
                                    updateTimePeriod={updateTimePeriod}
                                    typeId={typeId || 0}
                                    historyType='crypto'
                                    change={history?.change}
                                    history={
                                        (history?.history as HistoryDataProps['history']) || null
                                    }
                                    timeframes={timeframes}
                                    changes={data?.changes as any}
                                    loading={historyPeriod !== timePeriod}
                                    failed={!!historyError}
                                    displayName={displayName || ''}
                                    symbol={symbol}
                                    closedRanges={readClosedRanges(history?.market_closed_ranges)}
                                />
                            </TabsContent>
                            <TabsContent value='trading-view' className='mt-0 min-h-[580px]'>
                                <LazyBitycleChart
                                    slug={(symbol || slug || '').toUpperCase()}
                                    symbolType={symbolType}
                                    typeId={typeId}
                                    isStable={
                                        typeId === 2 ||
                                        (symbol || slug || '').toUpperCase() === 'USDT'
                                    }
                                />
                            </TabsContent>
                        </Tabs>
                    </section>

                    {(exchangeList?.length ?? 0) > 0 && (
                        <Section
                            id='exchanges-section'
                            title={`مقایسه قیمت ${displayName} در صرافی‌های ایرانی`}
                            className='order-2 lg:order-none lg:col-start-1 bg-gray-100/70 dark:bg-white/5 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full min-w-0 max-lg:mt-0'
                            isHidden={!hasExchange}
                        >
                            <SingleCoinExchangesListLive
                                name_fa={nameFa || ''}
                                symbol={slug}
                                type={typeId || 0}
                                initialExchangeList={exchangeList}
                            />
                        </Section>
                    )}

                    <Section
                        id='about-section'
                        title={`معرفی و آمار کلیدی ${displayName}`}
                        className='order-3 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0 max-lg:mt-0'
                    >
                        <SingleCoinIntro
                            change={history?.change}
                            marketCap={marketCap}
                            name={name || ''}
                            nameFa={nameFa}
                            supply={supply}
                            price={price}
                            rank={rank}
                            dominance={dominance}
                            symbol={symbol || ''}
                            volume24h={volume24h}
                            typeId={typeId}
                        />
                    </Section>

                    <aside
                        className={`contents lg:flex lg:flex-col lg:gap-6 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky z-10 transition-all ease-in-out duration-500 ${
                            isScrolledUp ? 'lg:top-[244px]' : 'lg:top-[180px]'
                        }`}
                    >
                        <section
                            id='market-table-section'
                            className='order-4 lg:order-none bg-gray-100/70 dark:bg-white/5 lg:mx-0 px-4 sm:px-6 lg:p-5 py-5 lg:py-5 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full'
                        >
                            <h3 className='font-bold text-sm dark:text-white mb-3 flex items-center justify-between'>
                                <span>اطلاعات جامع بازار {displayName}</span>
                            </h3>
                            <LazySingleCoinTableStatus {...(data || {})} />
                        </section>

                        {((sentiment?.sentimentVotesUpPercentage &&
                            sentiment?.sentimentVotesUpPercentage > 0) ||
                            investorGrade ||
                            traderGrade) && (
                            <section
                                id='sentiment-section'
                                className='order-5 lg:order-none bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-5 py-5 lg:py-5 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full'
                            >
                                <h3 className='font-bold text-sm dark:text-white mb-3 pb-2 border-b border-gray-100 dark:border-white/5'>
                                    تحلیل شاخص احساسات و گرید سرمایه‌گذاری
                                </h3>
                                <div className='grid grid-cols-3 gap-1 lg:gap-2 relative'>
                                    {sentiment?.sentimentVotesUpPercentage &&
                                        sentiment?.sentimentVotesUpPercentage > 0 && (
                                            <LazySingleCoinSentiment
                                                sentiment={sentiment}
                                                displayName={displayName || ''}
                                            />
                                        )}
                                    {investorGrade && (
                                        <LazySingleCoinInvestorGrade
                                            investorGrade={investorGrade}
                                        />
                                    )}
                                    {traderGrade && (
                                        <LazySingleCoinTMGrade traderGrade={traderGrade} />
                                    )}
                                </div>
                            </section>
                        )}

                        <section
                            id='prediction-section'
                            className='order-6 lg:order-none bg-gray-100/70 dark:bg-white/5 lg:mx-0 px-4 sm:px-6 lg:p-5 py-5 lg:py-5 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full'
                        >
                            <h3 className='font-bold text-sm dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-white/10'>
                                پیش‌بینی هوشمند قیمت {displayName}
                            </h3>
                            <LazySingleCoinPrediction />
                        </section>
                    </aside>

                    <section
                        id='converter-section'
                        className='order-7 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-0 py-5 lg:py-0 w-auto lg:w-full min-w-0'
                    >
                        <LazySingleCoinExchange price={price?.usdt_price || 0} initialCoin={data} />
                    </section>

                    {content && (
                        <section
                            id='blog-content-section'
                            className='order-8 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                        >
                            <div
                                className={`relative transition-all duration-500 ease-in-out overflow-hidden ${
                                    isExpanded ? 'max-h-none pb-12' : 'max-h-[360px]'
                                }`}
                            >
                                {featuredImage && (
                                    <Image
                                        width={1200}
                                        height={800}
                                        src={featuredImage?.node?.sourceUrl || ''}
                                        title={featuredImage?.node?.title || displayName}
                                        alt={featuredImage?.node?.title || displayName}
                                        className='aspect-video w-full rounded-2xl mb-6 object-cover shadow-sm'
                                    />
                                )}

                                <article className='prose post-content max-w-none text-justify dark:text-white mx-auto'>
                                    {tocItems && tocItems.length > 0 && (
                                        <PostToc
                                            tocItems={tocItems}
                                            className='bg-blue-50/50 dark:bg-white/5 border border-blue-100 dark:border-white/10 p-4 rounded-xl mb-6'
                                        />
                                    )}
                                    <MainContent
                                        processedContent={content || ''}
                                        className={undefined}
                                        title={nameFa || name || ''}
                                    />
                                    {posts_acf?.faqList && posts_acf.faqList.length > 0 && (
                                        <PostFAQ faqList={posts_acf.faqList} />
                                    )}
                                </article>

                                {!isExpanded && (
                                    <div className='absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-100/90 via-gray-100/70 to-transparent lg:from-white lg:via-white/80 dark:from-primary-active dark:via-primary-active/80 pointer-events-none' />
                                )}
                            </div>

                            <button
                                type='button'
                                onClick={() => setIsExpanded((prev) => !prev)}
                                className='w-fit flex items-center justify-center text-center mx-auto bg-blue-50 dark:bg-white/10 hover:bg-blue-100 dark:hover:bg-white/15 text-blue-600 dark:text-white cursor-pointer transition-all gap-2 border border-blue-200 dark:border-white/10 rounded-xl py-2.5 px-5 shadow-sm text-sm font-medium z-10 sticky bottom-4 mt-4'
                            >
                                <DocumentText size='20' className='shrink-0' />
                                <span>
                                    {isExpanded
                                        ? 'بستن متن کامل'
                                        : `مشاهده مقاله و معرفی کامل ${nameFa || name}`}
                                </span>
                                <ArrowDown2
                                    size='16'
                                    className={`transition-transform duration-300 ${
                                        isExpanded ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>
                        </section>
                    )}

                    <Section
                        id='comments-section'
                        title={`نظرات و دیدگاه کاربران درباره ${displayName}`}
                        className='order-9 lg:order-none lg:col-start-1 bg-white dark:bg-primary-active lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm w-auto lg:w-full min-w-0'
                    >
                        <PostComments
                            comments={comments}
                            hasNextPage={hasNextPage}
                            displayName={displayName}
                            currentPostId={databaseId || 0}
                            postRatings={postRatings}
                            commentCount={commentCount || 0}
                        />
                    </Section>

                    <Section
                        id='related-section'
                        title={`مقالات مرتبط با ${displayName}`}
                        className='order-10 lg:order-none lg:col-start-1 bg-gray-100/70 dark:bg-white/5 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm lg:bg-white lg:dark:bg-primary-active w-auto lg:w-full min-w-0'
                    >
                        <SingleCoinExistRelatedPost
                            categoryName={slug}
                            displayName={displayName || ''}
                            initialData={singleCoinRelatedPosts}
                        />
                    </Section>

                    {symbol !== 'USDT' && (
                        <div className='order-11 lg:order-none lg:col-start-1 w-auto lg:w-full min-w-0'>
                            <BackToButton title='بازگشت به لیست بازار ارزهای دیجیتال' />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UniversalCoinPageRender;
